import { enrichRecord } from "../../utils/recordUtils";

export function getDefaultDepartmentValue({
  allDepartments,
  selectedDepartment,
  userDeptName,
}) {
  if (allDepartments.length === 1) {
    return allDepartments[0];
  }
  if (selectedDepartment && selectedDepartment !== "All") {
    return selectedDepartment;
  }
  return allDepartments[0] || userDeptName || "청년부";
}

const KOREAN_NUMERIC_COLLATOR = new Intl.Collator("ko", {
  numeric: true,
  sensitivity: "base",
});

function getAcademicGradeWeight(value) {
  if (!value) return 9999;
  const normalized = String(value).trim();
  if (!normalized) return 9999;

  const schoolGradeMatch = normalized.match(/(초|중|고)\s*(\d)/);
  if (schoolGradeMatch) {
    const school = schoolGradeMatch[1];
    const grade = parseInt(schoolGradeMatch[2], 10);
    const base = school === "초" ? 10 : school === "중" ? 20 : 30;
    return base + grade;
  }

  return 9999;
}

function getClassSortWeight(classStr) {
  if (!classStr) return 9999;
  const normalized = String(classStr).trim();
  if (!normalized) return 9999;

  const gradeWeight = getAcademicGradeWeight(normalized);
  const classMatch = normalized.match(/(\d+)\s*반$/);

  if (gradeWeight !== 9999 && classMatch) {
    return gradeWeight * 100 + parseInt(classMatch[1], 10);
  }

  if (classMatch) return parseInt(classMatch[1], 10);

  if (gradeWeight !== 9999) return gradeWeight * 100;

  const anyNumberMatch = normalized.match(/(\d+)/);
  if (anyNumberMatch) return parseInt(anyNumberMatch[1], 10);

  return 9999;
}

function getStudentGradeSortWeight(student) {
  const classGradeWeight = getAcademicGradeWeight(student?.class || "");
  if (classGradeWeight !== 9999) return classGradeWeight;
  return getAcademicGradeWeight(student?.grade || student?.department || "");
}

export function buildSortedStudents({
  students,
  searchTerm,
  sortKey,
  sortOrder,
  selectedDepartment,
  visitLogs,
  attendanceLogs,
  matchSearch,
}) {
  const deptFilter = selectedDepartment || "All";

  // 1. Filter and Map first to include computed fields for sorting
  let result = students
    .filter(
      (s) =>
        (matchSearch(s.name, searchTerm) ||
          matchSearch(s.school || "", searchTerm) ||
          (s.phone && s.phone.includes(searchTerm))) &&
        (deptFilter === "All" ||
          deptFilter === "전체" ||
          (s.department || s.grade) === deptFilter)
    )
    .map((s) => enrichRecord(s, attendanceLogs, visitLogs));

  // 2. Sort based on the mapping
  result.sort((a, b) => {
    let comparison = 0;

    if (sortKey === "grade") {
      const gradeWeightA = getStudentGradeSortWeight(a);
      const gradeWeightB = getStudentGradeSortWeight(b);
      if (gradeWeightA !== gradeWeightB) {
        comparison = gradeWeightA - gradeWeightB;
      } else {
        comparison = KOREAN_NUMERIC_COLLATOR.compare(a.class || "", b.class || "");
      }
    } else if (sortKey === "class") {
      const classWeightA = getClassSortWeight(a.class || "");
      const classWeightB = getClassSortWeight(b.class || "");
      if (classWeightA !== classWeightB) {
        comparison = classWeightA - classWeightB;
      } else {
        comparison = KOREAN_NUMERIC_COLLATOR.compare(a.class || "", b.class || "");
      }
    } else if (sortKey === "lastAttendance" || sortKey === "lastVisit") {
      const valA = a[sortKey] ? new Date(a[sortKey]).getTime() : 0;
      const valB = b[sortKey] ? new Date(b[sortKey]).getTime() : 0;
      comparison = valA - valB;
    } else if (sortKey === "birth") {
      const valA = a.birth || "9999-99-99";
      const valB = b.birth || "9999-99-99";
      comparison = valA.localeCompare(valB);
    } else {
      const valA = String(a[sortKey] || "");
      const valB = String(b[sortKey] || "");
      comparison = KOREAN_NUMERIC_COLLATOR.compare(valA, valB);
    }

    return sortOrder === "asc" ? comparison : -comparison;
  });

  return result;
}

export function buildStudentStats({ students, attendanceLogs }) {
  const newStudentsCount = students.filter((s) => s.faithState === "초신자").length;

  const recentLogs = [...attendanceLogs]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 4);

  let avgAttendance = 0;
  if (recentLogs.length > 0) {
    const totalPresent = recentLogs.reduce((sum, log) => {
      const presentCount = Object.values(log).filter(
        (v) => v === "present" || v === "late"
      ).length;
      return sum + presentCount;
    }, 0);
    avgAttendance = Math.round(totalPresent / recentLogs.length);
  }

  return { newStudentsCount, avgAttendance };
}
